import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { Testimonial } from '@pages/LandingPage/components/Testimonial'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  containerSx,
  contentGridSx,
  dividerWrapperSx,
  findPerfectTechMatchLeftSideContentSx,
  findPerfectTechMatchLeftSideWrapperSx,
  findPerfectTechMatchRightSideImageSx,
  findPerfectTechMatchRightSideWrapperSx,
  protectYourPrivacyAndControlLeftSideImageSx,
  protectYourPrivacyAndControlLeftSideWrapperSx,
  protectYourPrivacyAndControlRightSideContentSx,
  protectYourPrivacyAndControlRightSideWrapperSx,
  protectYourPrivacyAndControlTestimonialSx,
  saveTimeAndStreamlineYourEvaluationProcessLeftSideContentSx,
  saveTimeAndStreamlineYourEvaluationProcessLeftSideWrapperSx,
  saveTimeAndStreamlineYourEvaluationProcessRightSideImageSx,
  saveTimeAndStreamlineYourEvaluationProcessRightSideWrapperSx,
  saveTimeAndStreamlineYourEvaluationProcessTestimonialSx,
  secondDividerWrapperSx,
  titleSx,
} from './styles'

const ContentSection = () => {
  const { t } = useTranslation()

  const { ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="tech-vendors-benefits-content-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={findPerfectTechMatchLeftSideWrapperSx}
        >
          <Stack sx={findPerfectTechMatchLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Targeted lead generation')}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                "Our precision search and matchmaking ensure that you connect with Tech Customers who are actively seeking the solutions and services you offer. No more wasted time on disinterested prospects. In our matchmaking marketplace, the right leads come to you. Showcase your solutions, services, and expertise to a highly engaged audience that's ready to buy."
              )}
            </Typography>

            <Testimonial
              testimonialText={t(
                'Long term sustained business success is about adapting to market trends and changes in buying behavior. The Launchpad’s Tech Matching has allowed our firm to connect to qualified buyers in the way qualified buyers want to interact and conduct their due diligence. Sell the way your buyers want to buy. Two leads in the first 24 hours and more every day!'
              )}
              personName="Mark Mahovlich"
              personPosition={t('VP of Strategy & Execution')}
            />
          </Stack>
        </Box>
        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={findPerfectTechMatchRightSideWrapperSx}
        >
          <Box
            alt={t('My Leads and Leads Profile pages screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-my-leads-and-leads-profile-pages.webp"
            sx={
              {
                ...findPerfectTechMatchRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box gridColumn="span 12" sx={dividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={protectYourPrivacyAndControlLeftSideWrapperSx}
        >
          <Box
            alt={t('Tech Vendor Solution View page screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-solution-view-page.webp"
            sx={
              {
                ...protectYourPrivacyAndControlLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={protectYourPrivacyAndControlRightSideWrapperSx}
        >
          <Stack sx={protectYourPrivacyAndControlRightSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t(
                'Break free from outdated approaches & embrace the future of B2B tech matchmaking '
              )}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                "Bid farewell to traditional lead generation methods that yield lackluster results. Instead of bombarding Tech Customers with unsolicited sales messages, we empower them to drive the search and matching process. They search for precisely what they need, based on their unique criteria. And guess what? If they accept a match with you, it means they're genuinely interested in what you have to offer. Stay ahead of the competition and be at the forefront of new lead generation methods."
              )}
            </Typography>
            <Testimonial
              testimonialText={t(
                'We just concluded an intro call with this lead. The match was a good fit. They requested a demo and pricing – small but solid user opportunity to begin with, but with a growing organization. Our reseller was very pleased with the lead. Thanks again for this lead …keep them coming!'
              )}
              personName="Karl F."
              personPosition={t('Channel Development Representative')}
              containerSx={protectYourPrivacyAndControlTestimonialSx}
            />
          </Stack>
        </Box>

        <Box gridColumn="span 12" sx={secondDividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={saveTimeAndStreamlineYourEvaluationProcessLeftSideWrapperSx}
        >
          <Stack
            sx={saveTimeAndStreamlineYourEvaluationProcessLeftSideContentSx}
          >
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Close More Deals')}
            </TLGTypography>
            <Typography variant="body1">
              {t(
                'Engage with Tech Customers who are genuinely interested in your solutions. Your matches are Tech Customers who have seen your profile, reviewed your solutions, and are ready to connect. These inherently closable leads will help you achieve revenue targets faster.'
              )}
            </Typography>

            <Testimonial
              testimonialText={t(
                'We won a deal at this account, $21K of endpoint protection. The match was spot on, very solid lead.'
              )}
              personName="Chris E."
              personPosition={t('Vice President of Marketing')}
              containerSx={
                saveTimeAndStreamlineYourEvaluationProcessTestimonialSx
              }
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={saveTimeAndStreamlineYourEvaluationProcessRightSideWrapperSx}
        >
          <Box
            alt={t('Tech Vendor Market Trends page screenshot')}
            loading="lazy"
            component="img"
            src="/images/tech-vendor-market-trends.webp"
            sx={{
              ...saveTimeAndStreamlineYourEvaluationProcessRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export { ContentSection }
